import * as React from 'react'

import { Tooltip as TooltipPrimitive } from 'radix-ui'

import { cn } from './cn'

export interface TooltipProps {
  title: React.ReactNode
  children: React.ReactElement
  placement?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'left-start'
    | 'left-end'
    | 'right-start'
    | 'right-end'
  arrow?: boolean
  enterDelay?: number
  open?: boolean
  className?: string
  /** Convenience: click handler attached to the trigger. */
  onClick?: React.MouseEventHandler
  /** Accepted for compatibility; not rendered. */
  role?: string
}

function placementToRadix(placement: TooltipProps['placement']): {
  side: 'top' | 'bottom' | 'left' | 'right'
  align: 'start' | 'center' | 'end'
} {
  const [side, alignRaw] = (placement || 'bottom').split('-') as [
    'top' | 'bottom' | 'left' | 'right',
    'start' | 'end' | undefined,
  ]
  return { side, align: alignRaw || 'center' }
}

const Tooltip = ({
  title,
  children,
  placement,
  arrow = true,
  enterDelay = 100,
  open,
  className,
  onClick,
}: TooltipProps) => {
  const { side, align } = placementToRadix(placement)

  // An empty title should render no tooltip, matching MUI.
  if (title === '' || title === null || title === undefined || title === false)
    return children

  return (
    <TooltipPrimitive.Provider delayDuration={enterDelay}>
      <TooltipPrimitive.Root open={open}>
        <TooltipPrimitive.Trigger asChild onClick={onClick}>
          {children}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            align={align}
            sideOffset={6}
            className={cn(
              'z-[1500] max-w-xs rounded-lg border border-white/10 bg-[#211826]/95 px-2.5 py-1.5 text-xs font-medium text-white shadow-menu backdrop-blur-sm',
              'animate-ps-pop-in select-none',
              className,
            )}
          >
            {title}
            {arrow && <TooltipPrimitive.Arrow className="fill-[#211826]/95" />}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  )
}

export default Tooltip
